import { useCallback, useEffect, useMemo, useState } from 'react';
import { computeLine, computeSaleTotals, formatMYR } from '@72on/domain';
import type { PosStore, VariantWithProduct } from '../offline/store.js';
import {
  deviceStatus,
  fetchSnapshotWithDevice,
  pushPending,
  requestDiscountApproval,
  signPayload,
} from '../offline/sync.js';
import {
  openDrawer,
  printReceipt,
  printReport,
  receiptJobFromSale,
  recordDrawerOpen,
} from '../offline/agent.js';
import { clearAdminSession } from '../offline/admin-session.js';
import { qtyMilliFromWeight, weightStep } from '../offline/weight.js';
import type { ClosedShift } from '../offline/shift.js';
import type { PaymentMethod, PaymentRecord, SaleLineRecord, SaleRecord } from '../offline/types.js';

interface Props {
  store: PosStore;
  onReset: () => void;
  onAdmin: () => void;
}

interface CartLine {
  key: string;
  variantId: string | null;
  description: string;
  qtyMilli: number;
  unit: VariantWithProduct['unit'];
  unitPriceSen: number;
  taxRateBp: number;
  isWeighted: boolean;
  /** Diskaun baris dalam sen. Diskaun, bukan harga baharu. */
  discountSen: number;
  /** Sebab diskaun; wajib apabila discountSen > 0. */
  discountReason: string | null;
}

interface CompletedSale {
  sale: SaleRecord;
  lines: SaleLineRecord[];
  payments: PaymentRecord[];
}

import { API_BASE } from '../config.js';
import { CashierGate } from './CashierGate.js';
import { sessionStillValid } from '../offline/staff.js';
import { uploadShift } from '../offline/shift-upload.js';

export function PosScreen({ store, onReset, onAdmin }: Props): React.ReactElement {
  const [query, setQuery] = useState('');
  const [variants, setVariants] = useState<VariantWithProduct[]>([]);
  const [cart, setCart] = useState<CartLine[]>([]);
  const [taxInclusive, setTaxInclusive] = useState(false);
  /**
   * Cukai jualan dihidupkan oleh pemilik? Lalai mati.
   *
   * Ini MESTI digunakan dalam kiraan paparan, bukan hanya dalam simpan jualan. Jika tidak, POS
   * memaparkan harga bercukai sementara jualan yang disimpan tidak bercukai — kasir mengambil wang
   * mengikut paparan, dan resit bercetak berbeza daripada skrin.
   */
  const [taxEnabled, setTaxEnabled] = useState(false);
  /** Kepala dan footer resit daripada tetapan kedai. */
  const [receiptHeader, setReceiptHeader] = useState<string[]>([]);
  const [receiptFooter, setReceiptFooter] = useState('');
  const [pending, setPending] = useState(0);
  /** Nombor peranti daripada pairing — dipaparkan supaya kasir tahu PC mana ini. */
  const [deviceNo, setDeviceNo] = useState<number | null>(null);
  /** Kasir yang sedang log masuk. Kosong = jualan tidak dikaitkan dengan nama. */
  const [cashier, setCashier] = useState('');
  /** ID kasir. Syif mesti merekod SIAPA yang membukanya, bukan sekadar nama. */
  const [cashierUserId, setCashierUserId] = useState('');
  const [gateOpen, setGateOpen] = useState(false);
  /**
   * Syif sudah ditutup, tetapi Z-report masih dipaparkan.
   *
   * Skrin pilih kasir TIDAK dibuka serta-merta: ia akan menutupi laporan yang baru dicetak, dan
   * kasir mesti melihat kiraan tunainya dahulu. Serahan berlaku apabila dia menutup laporan.
   */
  const [pendingHandover, setPendingHandover] = useState(false);
  /** Nama kedai. Nombor peranti boleh sama antara kedai, jadi nama diperlukan untuk membezakan. */
  const [shopName, setShopName] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<CompletedSale | null>(null);
  const [printNote, setPrintNote] = useState<string | null>(null);
  /** Peranti dicabut: jualan tidak boleh diterima, jadi jualan mesti dihentikan. */
  const [revoked, setRevoked] = useState(false);
  /** Peranti menunggu kelulusan pengurus: tidak boleh menerima wang. */
  const [pendingApproval, setPendingApproval] = useState(false);

  const [agentToken, setAgentToken] = useState<string | undefined>(undefined);
  const [shiftOpen, setShiftOpen] = useState(false);
  const [shiftModal, setShiftModal] = useState<'open' | 'close' | null>(null);
  /**
   * Senarai jualan untuk cetak semula.
   *
   * Kasir memerlukannya di POS, bukan di back office: pelanggan kembali ke kaunter meminta salinan,
   * dan kasir tidak sepatutnya perlu log masuk pemilik untuk mencetak resit.
   */
  const [salesOpen, setSalesOpen] = useState(false);
  const [salesRows, setSalesRows] = useState<
    Array<{ sale: SaleRecord; lines: SaleLineRecord[]; payments: PaymentRecord[] }>
  >([]);
  const [salesBusy, setSalesBusy] = useState(false);
  const [reprintBusy, setReprintBusy] = useState<string | null>(null);
  /**
   * Jualan yang sedang dipratonton.
   *
   * Kasir mesti memeriksa resit SEBELUM kertas keluar: cetak semula yang tersalah pilih membazir
   * kertas dan mengelirukan pelanggan. Pratonton menggunakan data yang SAMA dengan kerja cetakan.
   */
  const [previewEntry, setPreviewEntry] = useState<{
    sale: SaleRecord;
    lines: SaleLineRecord[];
    payments: PaymentRecord[];
  } | null>(null);
  const [cashInput, setCashInput] = useState('');
  const [closedShift, setClosedShift] = useState<ClosedShift | null>(null);

  const [weightFor, setWeightFor] = useState<VariantWithProduct | null>(null);
  const [weightInput, setWeightInput] = useState('');

  const [paying, setPaying] = useState(false);
  const [method, setMethod] = useState<PaymentMethod>('cash');
  const [paidInput, setPaidInput] = useState('');
  const [busy, setBusy] = useState(false);

  /**
   * Diskaun baris.
   *
   * Diskaun direkod sebagai diskaun (bukan harga baharu) supaya laporan tunai sepadan dan kos
   * diskaun kelihatan. Setiap diskaun memerlukan PIN pengurus — kelulusan itu direkod pada jualan.
   */
  const [discountFor, setDiscountFor] = useState<CartLine | null>(null);
  const [discountAmount, setDiscountAmount] = useState('');
  const [discountReason, setDiscountReason] = useState('Pelanggan tetap');
  const [discountPin, setDiscountPin] = useState('');
  const [discountError, setDiscountError] = useState<string | null>(null);
  const [discountBusy, setDiscountBusy] = useState(false);
  /** Pelulus yang disahkan untuk jualan ini. */
  const [approvedBy, setApprovedBy] = useState<{ userId: string; name: string } | null>(null);
  /** Token sekali guna daripada pelayan; bukti kelulusan yang disahkan. */
  const [approvalToken, setApprovalToken] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const [list, settings, count, token, shift] = await Promise.all([
      store.searchVariants(query),
      store.getSettings(),
      store.pendingCount(),
      store.getAgentToken(),
      store.getOpenShift(),
    ]);
    setVariants(list);
    setTaxInclusive(settings.tax_inclusive === true);
    setTaxEnabled(settings.tax_enabled === true);
    setReceiptHeader(settings.receipt_header ?? []);
    setReceiptFooter(settings.receipt_footer ?? '');

    /*
     * Tetapan setempat boleh basi jika pemilik menukar kepala resit selepas POS dibuka. Ambil
     * semula daripada pelayan; jika offline, tetapan setempat kekal digunakan.
     */
    void (async () => {
      try {
        const identity = await store.getIdentity();
        const keys = await store.getKeys();
        if (identity === undefined || keys === undefined) return;
        const fresh = await deviceStatus(API_BASE, identity.deviceId, keys.privateKey);
        if (fresh?.receiptHeader !== undefined && fresh.receiptHeader.length > 0) {
          setReceiptHeader(fresh.receiptHeader);
        }
        if (fresh?.receiptFooter !== undefined && fresh.receiptFooter !== '') {
          setReceiptFooter(fresh.receiptFooter);
        }
      } catch {
        // Offline: guna tetapan setempat.
      }
    })();
    setPending(count);
    setAgentToken(token);
    setShiftOpen(shift !== undefined);
  }, [store, query]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /**
   * Semak status peranti sebaik POS dibuka.
   *
   * Peranti yang dicabut tidak boleh menghantar jualan. Jika kita hanya mengetahuinya selepas sync
   * pertama, kasir boleh menerima wang selama itu — dan setiap jualan itu hilang daripada rekod.
   */
  useEffect(() => {
    void (async () => {
      const identity = await store.getIdentity();
      // Nombor peranti dipaparkan di header. Ia diberikan oleh pelayan semasa pairing dan tidak
      // boleh diubah sesuka hati seperti nama — jadi ia penanda yang jujur tentang PC mana ini.
      if (identity !== undefined) {
        setDeviceNo(identity.deviceNo);
        // Nama kedai mungkin belum ada pada identiti peranti lama; ia diisi semula oleh
        // `applySnapshot` pada segarkan katalog pertama.
        setShopName(identity.tenantName ?? '');
      }
      const keys = await store.getKeys();
      if (identity === undefined || keys === undefined) return;
      const status = await deviceStatus(API_BASE, identity.deviceId, keys.privateKey);
      if (status === null) return;

      // Tetapan cukai terkini daripada pelayan: mengubahnya di back office berkuat kuasa pada
      // pembukaan POS seterusnya, tanpa perlu menekan "Segarkan katalog".
      if (status.taxEnabled !== undefined) setTaxEnabled(status.taxEnabled);
      if (status.taxInclusive !== undefined) setTaxInclusive(status.taxInclusive);

      // Kepala/footer resit yang TERKINI: perubahan di back office berkuat kuasa pada cetakan
      // seterusnya, tanpa menekan "Segarkan katalog".
      if (status.receiptHeader !== undefined) setReceiptHeader(status.receiptHeader);
      if (status.receiptFooter !== undefined) setReceiptFooter(status.receiptFooter);
      if (status.status === 'pending') setPendingApproval(true);
      else if (status.status !== 'active') setRevoked(true);
    })();
  }, [store]);

  useEffect(() => {
    void (async () => {
      /**
       * Syif terbuka menentukan SIAPA yang sedang bekerja.
       *
       * Ini mengatasi nama yang tersimpan dalam pelayar: jika syif dibuka oleh A dan pelayar masih
       * menyimpan nama B (contohnya selepas pelayar dimulakan semula), orang yang betul ialah A —
       * dialah yang memegang laci wang sehingga syif ditutup.
       */
      const openShift = await store.getOpenShift();
      if (openShift !== undefined && (openShift.cashier_name ?? '') !== '') {
        setCashier(openShift.cashier_name ?? '');
        setCashierUserId(openShift.cashier_user_id ?? '');
        window.localStorage.setItem('72on:cashier', openShift.cashier_name ?? '');
        return;
      }

      const saved = window.localStorage.getItem('72on:cashier');
      if (saved !== null) {
        // Sahkan sesi masih hidup. Sesi PIN luput selepas 12 jam; tanpa semakan ini POS akan terus
        // memaparkan nama kasir walaupun pelayan sudah tidak mengaitkan jualan dengan sesiapa.
        const token = await store.getCashierToken();
        if (token !== undefined && (await sessionStillValid(API_BASE, token))) {
          setCashier(saved);
          setCashierUserId(window.localStorage.getItem('72on:cashier_id') ?? '');
          return;
        }
        window.localStorage.removeItem('72on:cashier');
        window.localStorage.removeItem('72on:cashier_id');
        await store.clearCashierToken();
      }

      // Tiada kasir tersimpan = belum ada yang log masuk pada PC ini untuk syif ini. Sesi pemilik
      // (back office) TIDAK dikira: ia bukan tanda bahawa seseorang sedang bekerja di kaunter.
      // Skrin itu membenarkan "teruskan", jadi jualan tidak pernah disekat oleh ciri atribusi.
      setGateOpen(true);
    })();
  }, [store]);

  const totals = useMemo(() => {
    if (cart.length === 0) return null;
    try {
      return computeSaleTotals({
        lines: cart.map((line) => ({
          qtyMilli: line.qtyMilli,
          unitPriceSen: line.unitPriceSen,
          // Cukai dimatikan → kadar 0, sama seperti yang disimpan dan dihantar ke pelayan.
          taxRateBp: taxEnabled ? line.taxRateBp : 0,
          taxInclusive,
          ...(line.discountSen > 0 ? { discountSen: line.discountSen } : {}),
        })),
      });
    } catch {
      return null;
    }
  }, [cart, taxInclusive, taxEnabled]);

  function addVariant(variant: VariantWithProduct): void {
    if (variant.is_weighted) {
      setWeightFor(variant);
      setWeightInput('');
      return;
    }
    setCart((current) => {
      const existing = current.find((line) => line.variantId === variant.id);
      if (existing !== undefined) {
        return current.map((line) =>
          line.key === existing.key ? { ...line, qtyMilli: line.qtyMilli + 1000 } : line,
        );
      }
      return [
        ...current,
        {
          key: crypto.randomUUID(),
          variantId: variant.id,
          description: `${variant.product_name}${variant.name === null ? '' : ` ${variant.name}`}`,
          qtyMilli: 1000,
          unit: variant.unit,
          unitPriceSen: variant.price_sen,
          taxRateBp: variant.tax_rate_bp ?? 0,
          isWeighted: false,
          discountSen: 0,
          discountReason: null,
        },
      ];
    });
  }

  function addWeighted(): void {
    if (weightFor === null) return;
    const grams = Number(weightInput.replace(',', '.'));
    if (!Number.isFinite(grams) || grams <= 0) {
      setMessage('Masukkan berat dalam gram');
      return;
    }
    setCart((current) => [
      ...current,
      {
        key: crypto.randomUUID(),
        variantId: weightFor.id,
        description: `${weightFor.product_name}${weightFor.name === null ? '' : ` ${weightFor.name}`}`,
        // Gram input ditukar mengikut unit: kg menyimpan 0.1 untuk 100 g, g menyimpan 100.
        qtyMilli: qtyMilliFromWeight(grams, weightFor.unit),
        unit: weightFor.unit,
        unitPriceSen: weightFor.price_sen,
        taxRateBp: weightFor.tax_rate_bp ?? 0,
        isWeighted: true,
        discountSen: 0,
        discountReason: null,
      },
    ]);
    setWeightFor(null);
    setWeightInput('');
    setMessage(null);
  }

  function openDiscount(line: CartLine): void {
    setDiscountFor(line);
    setDiscountAmount((line.discountSen / 100).toFixed(2).replace(/\.00$/, ''));
    setDiscountReason(line.discountReason ?? 'Pelanggan tetap');
    setDiscountPin('');
    setDiscountError(null);
  }

  async function applyDiscount(): Promise<void> {
    if (discountFor === null) return;
    setDiscountError(null);

    /*
     * Guna fungsi domain yang SAMA dengan pelayan.
     *
     * Math.round((qty/1000) * harga) boleh berbeza daripada pembundaran separuh-ke-atas integer
     * pada kuantiti berpecah (contohnya 1.5 g), lalu menghasilkan perbezaan jumlah yang menyebabkan
     * jualan ditandakan needs_review.
     */
    const grossSen = computeLine({
      qtyMilli: discountFor.qtyMilli,
      unitPriceSen: discountFor.unitPriceSen,
      taxRateBp: 0,
      taxInclusive: false,
    }).grossSen;
    const amountSen = Math.round(Number(discountAmount.replace(',', '.') || '0') * 100);

    if (!Number.isFinite(amountSen) || amountSen < 0) {
      setDiscountError('Jumlah diskaun tidak sah.');
      return;
    }
    if (amountSen > grossSen) {
      setDiscountError('Diskaun melebihi harga baris itu.');
      return;
    }

    // Diskaun sifar = buang diskaun sedia ada; tiada kelulusan diperlukan.
    if (amountSen === 0) {
      setCart((current) =>
        current.map((line) =>
          line.key === discountFor.key ? { ...line, discountSen: 0, discountReason: null } : line,
        ),
      );
      setDiscountFor(null);
      return;
    }

    if (discountPin.trim().length < 4) {
      setDiscountError('Masukkan PIN pengurus untuk meluluskan diskaun.');
      return;
    }

    setDiscountBusy(true);
    try {
      const identity = await store.getIdentity();
      const keys = await store.getKeys();
      if (identity === undefined || keys === undefined) {
        throw new Error('Peranti belum dipasangkan.');
      }

      const approver = await requestDiscountApproval(
        API_BASE,
        identity.deviceId,
        keys.privateKey,
        discountPin.trim(),
      );

      setApprovedBy({ userId: approver.user_id, name: approver.name });
      setApprovalToken(approver.token);
      setCart((current) =>
        current.map((line) =>
          line.key === discountFor.key
            ? { ...line, discountSen: amountSen, discountReason }
            : line,
        ),
      );
      setDiscountFor(null);
    } catch (caught) {
      setDiscountError(
        caught instanceof Error ? caught.message : 'PIN tidak sah atau rangkaian gagal.',
      );
    } finally {
      setDiscountBusy(false);
    }
  }

  /**
   * Kurang/tambah kuantiti pada baris cart.
   *
   * Kasir kerap mendengar "saya nak 8, bukan 10" selepas barang dimasukkan. Sebelum ini satu-satunya
   * cara ialah buang baris dan imbas semula, yang membuang diskaun dan menyusahkan pelanggan.
   */
  function changeQty(key: string, direction: 1 | -1): void {
    setCart((current) =>
      current.map((line) => {
        if (line.key !== key) return line;

        // Barang berat: 100 g setiap tekan, dalam unit yang betul; barang kira: satu unit.
        const step = line.isWeighted ? weightStep(line.unit) : 1000;
        const next = Math.max(step, line.qtyMilli + direction * step);
        if (next === line.qtyMilli) return line;

        const gross = computeLine({
          qtyMilli: next,
          unitPriceSen: line.unitPriceSen,
          taxRateBp: 0,
          taxInclusive: false,
        }).grossSen;

        // Diskaun tidak boleh melebihi harga baris: buang jika kuantiti berkurang terlalu banyak.
        const fits = line.discountSen <= gross;
        if (!fits) {
          setMessage('Diskaun baris dibuang kerana kuantiti berkurang.');
        }
        return {
          ...line,
          qtyMilli: next,
          discountSen: fits ? line.discountSen : 0,
          discountReason: fits ? line.discountReason : null,
        };
      }),
    );
  }

  function removeLine(key: string): void {
    setCart((current) => current.filter((line) => line.key !== key));
  }

  async function printSale(
    completed: CompletedSale,
    openDrawerOnCash: boolean,
    reprint = false,
  ): Promise<void> {
    if (agentToken === undefined) {
      setPrintNote('Tiada ejen cetak. Resit boleh dicetak selepas ejen dipasang.');
      return;
    }
    const job = receiptJobFromSale({
      sale: completed.sale,
      lines: completed.lines,
      payments: completed.payments,
      openDrawer: openDrawerOnCash && completed.payments.some((payment) => payment.method === 'cash'),
      // Kepala dan footer daripada tetapan kedai: pemilik mengubahnya di back office, bukan dengan
      // menyunting fail pada setiap PC.
      ...(receiptHeader.length === 0 ? {} : { receiptHeader }),
      ...(receiptFooter === '' ? {} : { receiptFooter }),
      reprint,
    });
    const outcome = await printReceipt(agentToken, job);
    setPrintNote(
      // `duplicate` bermaksud ejen menolak kerana `job_id` itu sudah dicetak. Ia bukan kejayaan:
      // tiada kertas keluar. Jangan sesekali melaporkannya sebagai berjaya.
      outcome.duplicate
        ? 'Resit ini sudah dicetak. Tekan "Cetak semula" untuk salinan baharu.'
        : outcome.printed
          ? reprint
            ? 'Resit dicetak semula'
            : 'Resit dicetak'
          : outcome.queued
            ? `Resit dibariskan: ${outcome.error ?? 'printer belum sedia'}`
            : `Cetak gagal: ${outcome.error ?? 'ralat tidak diketahui'}`,
    );
  }

  /**
   * Muatkan tetapan kedai daripada pelayan.
   *
   * Tetapan (cukai, kepala/footer resit) disemak setiap kali POS dibuka — tetapi jika POS kekal
   * terbuka sepanjang hari, atau dibuka semasa offline, ia boleh ketinggalan. Butang ini memberi
   * kasir kawalan: satu tekan, dan tetapan terkini diterapkan tanpa memuat semula halaman dan tanpa
   * mengganggu bakul yang sedang diisi.
   */
  async function reloadSettings(): Promise<void> {
    const identity = await store.getIdentity();
    const keys = await store.getKeys();
    if (identity === undefined || keys === undefined) {
      setMessage('Peranti ini tiada kunci. Pasangkan semula.');
      return;
    }

    const status = await deviceStatus(API_BASE, identity.deviceId, keys.privateKey);
    if (status === null) {
      // Rangkaian gagal ATAU peranti dicabut. Jangan tuntut berjaya.
      setMessage('Tetapan tidak dapat dimuatkan — semak sambungan internet.');
      return;
    }

    if (status.taxEnabled !== undefined) setTaxEnabled(status.taxEnabled);
    if (status.taxInclusive !== undefined) setTaxInclusive(status.taxInclusive);
    if (status.receiptHeader !== undefined) setReceiptHeader(status.receiptHeader);
    if (status.receiptFooter !== undefined) setReceiptFooter(status.receiptFooter);

    setMessage(
      status.receiptHeader !== undefined && status.receiptHeader.length > 0
        ? `Tetapan dimuatkan: cukai ${status.taxEnabled === true ? 'hidup' : 'mati'}, kepala resit "${status.receiptHeader[0] ?? ''}".`
        : `Tetapan dimuatkan: cukai ${status.taxEnabled === true ? 'hidup' : 'mati'}.`,
    );
  }

  // Toast hilang sendiri: mesej yang kekal selama-lamanya menutup skrin dan mengelirukan.
  useEffect(() => {
    if (message === null) return;
    const timer = window.setTimeout(() => setMessage(null), 5000);
    return () => window.clearTimeout(timer);
  }, [message]);

  /** Buka senarai jualan terkini (tempatan, jadi ia berfungsi walaupun offline). */
  async function openSalesList(): Promise<void> {
    setSalesOpen(true);
    setSalesBusy(true);
    try {
      setSalesRows(await store.salesForReprint(20));
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : 'gagal memuatkan senarai jualan');
    } finally {
      setSalesBusy(false);
    }
  }

  /**
   * Cetak semula resit daripada senarai.
   *
   * Laci TIDAK dibuka: cetak semula bukan transaksi tunai baharu, dan membuka laci setiap kali
   * salinan diminta akan menjadikan kawalan tunai tidak bermakna.
   */
  async function reprintFromList(
    entry: { sale: SaleRecord; lines: SaleLineRecord[]; payments: PaymentRecord[] },
  ): Promise<void> {
    if (agentToken === undefined) {
      setMessage('Tiada ejen cetak. Pasang ejen pada PC ini untuk mencetak.');
      return;
    }
    setReprintBusy(entry.sale.id);
    try {
      const job = receiptJobFromSale({
        sale: entry.sale,
        lines: entry.lines,
        payments: entry.payments,
        openDrawer: false,
        reprint: true,
        // Nama kasir yang MENJUAL, bukan yang kebetulan bertugas sekarang.
        ...(entry.sale.cashier_name == null || entry.sale.cashier_name === ''
          ? cashier === ''
            ? {}
            : { cashierName: cashier }
          : { cashierName: entry.sale.cashier_name }),
        ...(receiptHeader.length === 0 ? {} : { receiptHeader }),
        ...(receiptFooter === '' ? {} : { receiptFooter }),
      });
      const outcome = await printReceipt(agentToken, job);
      setMessage(
        outcome.printed
          ? `Resit ${entry.sale.receipt_no} dicetak semula.`
          : `Resit ${entry.sale.receipt_no} tidak dicetak: ${outcome.error ?? 'semak ejen'}.`,
      );
    } catch (caught) {
      setMessage(
        `Cetak semula gagal: ${caught instanceof Error ? caught.message : 'ralat tidak diketahui'}`,
      );
    } finally {
      setReprintBusy(null);
    }
  }

  async function sync(): Promise<void> {
    // Syif yang gagal dihantar (offline semasa tutup syif) dicuba semula di sini.
    void pushPendingShifts();
    const identity = await store.getIdentity();
    const keys = await store.getKeys();
    if (identity === undefined || keys === undefined) return;
    const outcome = await pushPending({
      store,
      apiBase: API_BASE,
      deviceId: identity.deviceId,
      privateKey: keys.privateKey,
    });
    if (outcome !== null && outcome.errorCode === 'DEVICE_PENDING') {
      setPendingApproval(true);
      setMessage('Peranti ini menunggu kelulusan pengurus. Jualan belum boleh direkodkan.');
      return;
    }
    if (outcome !== null && (outcome.errorCode === 'DEVICE_REVOKED' || outcome.status === 403)) {
      setRevoked(true);
      setMessage('Peranti ini telah dicabut. Jualan tidak boleh direkodkan — hubungi pemilik.');
      return;
    }
    if (outcome !== null && outcome.rejected.length > 0) {
      setMessage(`Pelayan menolak ${outcome.rejected.length} rekod: ${outcome.rejected[0]?.code ?? ''}`);
    }
    await refresh();
  }

  /**
   * Pasang semula peranti ini selepas ia dicabut.
   *
   * Jualan yang belum dihantar KEKAL dalam IndexedDB dan akan dihantar selepas peranti dipasangkan
   * semula — jadi ini bukan "kosongkan peranti". Kasir hanya perlu kod daripada pemilik.
   *
   * Nombor peranti dikekalkan oleh kod gantian, jadi resit tidak melompat ke nombor kaunter lain.
   */
  /**
   * Buka laci tanpa jualan (beri wang baki, tukar duit).
   *
   * Dua perkara yang menjadikannya selamat:
   * 1. **Kasir mesti dipilih.** Laci yang dibuka tanpa nama tidak boleh dijejak, dan kiraan tunai
   *    menjadi perdebatan apabila wang tidak sepadan.
   * 2. **Setiap pembukaan direkod** ke pelayan (audit_log, `drawer.opened`) bersama syif. Rekod ini
   *    tidak menyekat pembukaan — rangkaian mati tidak boleh menghalang kasir memberi wang baki.
   */
  async function openDrawerNow(): Promise<void> {
    if (agentToken === undefined) {
      setMessage('Ejen cetak tidak berjalan pada PC ini, jadi laci tidak boleh dibuka.');
      return;
    }
    if (cashier === '') {
      setMessage('Pilih kasir dahulu — buka laci mesti boleh dijejak kepada seseorang.');
      return;
    }

    const opened = await openDrawer(agentToken, `drawer-${Date.now()}`);
    if (!opened) {
      setMessage('Laci tidak terbuka. Semak sambungan ejen cetak dan kabel laci.');
      return;
    }
    setMessage('Laci dibuka (tanpa jualan) — direkod untuk syif ini.');

    const identity = await store.getIdentity();
    const keys = await store.getKeys();
    const shift = await store.getOpenShift();
    if (identity !== undefined && keys !== undefined) {
      // Best-effort: jangan biarkan kegagalan rangkaian menghalang kasir.
      void recordDrawerOpen(
        API_BASE,
        identity,
        keys.privateKey,
        shift?.id ?? null,
        signPayload,
      );
    }
  }

  async function beginRepair(): Promise<void> {
    const pending = await store.pendingOutbox(50);
    if (pending.length > 0) {
      const proceed = window.confirm(
        `Ada ${pending.length} jualan yang belum dihantar. Ia kekal pada PC ini dan akan dihantar selepas peranti dipasangkan semula. Teruskan?`,
      );
      if (!proceed) return;
    }
    onReset();
  }

  async function completeSale(): Promise<void> {
    if (totals === null) return;
    if (revoked) {
      setMessage('Peranti ini telah dicabut. Jualan tidak boleh direkodkan.');
      return;
    }
    if (pendingApproval) {
      setMessage('Peranti ini menunggu kelulusan pengurus. Jualan belum boleh direkodkan.');
      return;
    }
    /*
     * Syif WAJIB.
     *
     * Tanpa syif, jualan tidak boleh dikaitkan dengan sesiapa: laporan syif kosong, kiraan tunai
     * tidak lengkap, dan wang tidak boleh dijejak. Data produksi menunjukkan jualan diskaun yang
     * berlaku di luar syif dan hilang daripada laporan — itu yang kita halang di sini.
     */
    const openShiftForSale = await store.getOpenShift();
    if (openShiftForSale === undefined) {
      setMessage('Buka syif dahulu sebelum menjual. Setiap jualan mesti boleh dijejak kepada seseorang.');
      setShiftModal('open');
      return;
    }

    setBusy(true);
    setPrintNote(null);
    try {
      const paidSen = Math.round(Number(paidInput.replace(',', '.')) * 100);

    const completed = await store.completeSale({
      ...(openShiftForSale === undefined ? {} : { shiftId: openShiftForSale.id }),
      ...(cashier === '' ? {} : { cashierName: cashier }),
      ...(totals.discountSen === 0
        ? {}
        : {
            discountReason:
              cart.find((line) => line.discountSen > 0)?.discountReason ?? 'Diskaun kaunter',
            ...(approvedBy === null
              ? {}
              : {
                  discountApprovedBy: approvedBy.userId,
                  discountApprovedByName: approvedBy.name,
                  ...(approvalToken === null ? {} : { discountApproval: approvalToken }),
                }),
          }),
        lines: cart.map((line) => ({
          variantId: line.variantId,
          description: line.description,
          qtyMilli: line.qtyMilli,
          unit: line.unit,
          unitPriceSen: line.unitPriceSen,
          taxRateBp: line.taxRateBp,
          qtySource: line.isWeighted ? 'grams' : 'scan',
          ...(line.discountSen > 0 ? { discountSen: line.discountSen } : {}),
        })),
        payments: [
          {
            method,
            amountSen: Number.isFinite(paidSen) && paidSen > 0 ? paidSen : totals.totalSen,
          },
        ],
      });

      setReceipt({ sale: completed.sale, lines: completed.lines, payments: completed.payments });
      setCart([]);
      setApprovedBy(null);
      setApprovalToken(null);
      setPaying(false);
      setPaidInput('');
      await printSale(
        { sale: completed.sale, lines: completed.lines, payments: completed.payments },
        true,
      );
      void sync();
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : 'jualan gagal');
    } finally {
      setBusy(false);
    }
  }


  /**
   * Hantar syif ke pelayan, tanpa menyekat apa-apa.
   *
   * Jika ia gagal, syif kekal dengan `uploaded_at = null` dan dihantar pada sync berikutnya. Kedai
   * tidak boleh terhalang daripada menutup syif kerana rangkaian.
   */
  async function pushShift(shift: { id: string; client_seq: number } & Record<string, unknown>): Promise<void> {
    const identity = await store.getIdentity();
    const keys = await store.getKeys();
    if (identity === undefined || keys === undefined) return;

    const ok = await uploadShift(API_BASE, identity, keys.privateKey, shift as never, shift.client_seq);
    if (ok) await store.markShiftUploaded(shift.id);
  }

  /** Hantar semua syif yang belum diterima pelayan. */
  async function pushPendingShifts(): Promise<void> {
    const pending = await store.listUnuploadedShifts();
    for (const shift of pending) {
      const identity = await store.getIdentity();
      const keys = await store.getKeys();
      if (identity === undefined || keys === undefined) return;
      const ok = await uploadShift(
        API_BASE,
        identity,
        keys.privateKey,
        shift,
        shift.status === 'open' ? 1 : 2,
      );
      if (ok) await store.markShiftUploaded(shift.id);
    }
  }

  async function openShiftNow(): Promise<void> {
    /**
     * Syif tanpa nama ialah syif tanpa tanggungjawab.
     *
     * Jika tiada kasir (contohnya pemilik menekan "Teruskan tanpa kasir"), minta dia memilih nama
     * dahulu. Wang tunai dalam laci mesti ada pemilik yang jelas.
     */
    if (cashier === '') {
      setShiftModal(null);
      setCashInput('');
      setMessage('Pilih kasir dahulu — syif mesti ada nama di belakangnya.');
      setGateOpen(true);
      return;
    }

    const sen = Math.round(Number(cashInput.replace(',', '.')) * 100);
    if (!Number.isFinite(sen) || sen < 0) {
      setMessage('Masukkan tunai awal yang sah');
      return;
    }
    try {
      await store.openShift(
        sen,
        cashier === '' ? undefined : { userId: cashierUserId, name: cashier },
      );
      setShiftModal(null);
      setCashInput('');
      setShiftOpen(true);

      const opened = await store.getOpenShift();
      if (opened !== undefined) {
        void pushShift({ ...opened, client_seq: 1 });
      }
      if (agentToken !== undefined) {
        await openDrawer(agentToken, `shift-open-${Date.now()}`);
      }
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : 'gagal membuka syif');
    }
  }

  /**
   * Tukar kasir tanpa memasang semula peranti.
   *
   * Syif bertukar beberapa kali sehari, dan orang yang memegang PC bertukar bersamanya. Tanpa
   * butang ini, satu-satunya cara ialah memasang semula peranti — yang memadam identiti peranti dan
   * jualan yang belum dihantar.
   *
   * Ia mengosongkan sesi PIN juga: sesi itu milik kasir SEBELUM ini, dan membiarkannya bermakna
   * jualan orang baharu akan dikaitkan dengan nama orang lama.
   */
  async function switchCashier(): Promise<void> {
    /**
     * Syif ialah tempoh tanggungjawab satu orang ke atas laci wang.
     *
     * Menukar kasir semasa syif terbuka bermakna dua orang berkongsi satu kiraan tunai — dan jika
     * wang tidak sepadan pada waktu tutup, tiada cara untuk mengetahui siapa yang salah. Jadi
     * syif mesti ditutup dahulu, dengan kiraan tunai oleh orang yang memegangnya.
     */
    if (shiftOpen) {
      setMessage('Tutup syif dahulu — kiraan tunai mesti dibuat oleh kasir yang memegang laci.');
      setShiftModal('close');
      return;
    }

    window.localStorage.removeItem('72on:cashier');
    window.localStorage.removeItem('72on:cashier_id');
    await store.clearCashierToken();
    setCashier('');
    setCashierUserId('');
    setGateOpen(true);
  }

  /**
   * Selesaikan serahan kasir selepas Z-report ditutup.
   *
   * Sesi PIN dibuang: syif sudah tamat, jadi sesi itu tidak lagi mewakili sesiapa. Kasir seterusnya
   * mesti memilih namanya sendiri.
   */
  async function finishHandover(): Promise<void> {
    if (!pendingHandover) return;
    setPendingHandover(false);
    window.localStorage.removeItem('72on:cashier');
    window.localStorage.removeItem('72on:cashier_id');
    await store.clearCashierToken();
    setCashier('');
    setCashierUserId('');
    setGateOpen(true);
  }

  async function closeShiftNow(): Promise<void> {
    const sen = Math.round(Number(cashInput.replace(',', '.')) * 100);
    if (!Number.isFinite(sen) || sen < 0) {
      setMessage('Masukkan kiraan tunai yang sah');
      return;
    }
    try {
      const closed = await store.closeShift(sen);
      // Hantar versi TERTUTUP (kiraan tunai + beza), bukan versi terbuka.
      void pushShift({ ...closed.shift, client_seq: 2 });
      setClosedShift(closed);
      setShiftModal(null);
      setCashInput('');
      setShiftOpen(false);

      // Syif tamat bersama kasirnya, tetapi serahan berlaku SELEPAS Z-report ditutup. Jika skrin
      // pilih kasir muncul sekarang, ia menutupi laporan yang baru dicetak.
      setPendingHandover(true);

      if (agentToken !== undefined) {
        /*
         * Kepala dan footer diambil SEMULA daripada pelayan sebelum mencetak.
         *
         * Tetapan dalam storan setempat boleh basi jika pemilik menukar kepala resit selepas POS
         * dibuka. Z-report yang dicetak dengan kepala lama kelihatan seperti pepijat, sedangkan
         * resit jualan baharu sudah betul.
         */
        let headerForPrint = receiptHeader;
        let footerForPrint = receiptFooter;
        try {
          const identity = await store.getIdentity();
          const keys = await store.getKeys();
          if (identity !== undefined && keys !== undefined) {
            const fresh = await deviceStatus(API_BASE, identity.deviceId, keys.privateKey);
            if (fresh?.receiptHeader !== undefined && fresh.receiptHeader.length > 0) {
              headerForPrint = fresh.receiptHeader;
            }
            if (fresh?.receiptFooter !== undefined && fresh.receiptFooter !== '') {
              footerForPrint = fresh.receiptFooter;
            }
          }
        } catch {
          // Offline: guna tetapan setempat.
        }

        await printReport(agentToken, {
          job_id: `z-${closed.shift.id}`,
          kind: 'z_report',
          shift: {
            opened_at: closed.shift.opened_at,
            closed_at: closed.shift.closed_at,
            sales_count: closed.shift.sales_count,
            sales_total_sen: closed.shift.sales_total_sen,
            payment_breakdown: closed.shift.payment_breakdown,
            opening_cash_sen: closed.shift.opening_cash_sen,
            counted_cash_sen: closed.shift.counted_cash_sen ?? 0,
            expected_cash_sen: closed.shift.expected_cash_sen ?? 0,
            variance_sen: closed.shift.variance_sen ?? 0,
            discount_sen: closed.summary.discount_sen,
          },
          ...(headerForPrint.length === 0 ? {} : { receipt_header: headerForPrint }),
          ...(footerForPrint === '' ? {} : { receipt_footer: footerForPrint }),
        });
        await openDrawer(agentToken, `shift-close-${Date.now()}`);
      }
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : 'gagal menutup syif');
    }
  }

  /**
   * Segarkan katalog menggunakan kunci PERANTI.
   *
   * POS tidak lagi memegang sesi pemilik. Peranti yang dipasangkan boleh membaca katalog dengan
   * tandatangan Ed25519-nya sendiri — jadi tiada token pemilik disimpan dalam pelayar PC kaunter.
   * Laporan kewangan dan tetapan kekal memerlukan kata laluan pemilik di back office.
   */
  async function refreshCatalog(): Promise<void> {
    const identity = await store.getIdentity();
    const keys = await store.getKeys();
    if (identity === undefined || keys === undefined) {
      setMessage('Peranti ini belum dipasangkan. Sediakan peranti terlebih dahulu.');
      return;
    }
    try {
      const snapshot = await fetchSnapshotWithDevice(
        API_BASE,
        identity.deviceId,
        keys.privateKey,
      );
      await store.applySnapshot(snapshot);
      setMessage(`Katalog disegarkan (${snapshot.variants.length} varian)`);
      await refresh();
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : 'gagal menyegarkan katalog');
    }
  }

  return (
    <div className="pos">
      {gateOpen && (
        <CashierGate
          store={store}
          onDone={(name, userId) => {
            setCashier(name);
            setCashierUserId(userId);
            window.localStorage.setItem('72on:cashier', name);
            window.localStorage.setItem('72on:cashier_id', userId);
            setGateOpen(false);
          }}
          onSkip={() => setGateOpen(false)}
        />
      )}
      {/*
        Header tiga kumpulan: identiti (brand + kasir), konteks (kedai/peranti/sync/syif), dan
        tindakan. Memisahkannya mengelakkan butang bertaburan dan baris kedua yang bercampur.
      */}
      <header className="bar">
        <div className="bar-brand">
          <strong>72on POS</strong>
          {cashier !== '' ? (
            <>
              <span className="pill cashier" title="Jualan direkod atas nama kasir ini">
                Kasir: {cashier}
              </span>
              <button type="button" className="ghost" onClick={() => void switchCashier()}>
                Tukar kasir
              </button>
            </>
          ) : null}
        </div>

        <div className="bar-context">
          {/*
            Nombor peranti, bukan nama. Nama boleh diubah sesiapa di back office (dan "Kaunter 1"
            ialah nama lalai), jadi hanya nombor ini yang memberitahu kasir PC mana yang dia pakai.
          */}
          {deviceNo !== null && (
            <span className="pill device" title="Kedai dan nombor peranti daripada pairing">
              {shopName !== '' ? `${shopName} · ` : ''}PC #{deviceNo}
            </span>
          )}
          <span className={pending === 0 ? 'pill sync ok' : 'pill sync warn'}>
            {pending === 0 ? 'disinkronkan' : `${pending} belum dihantar`}
          </span>
          <span className={shiftOpen ? 'pill shift ok' : 'pill shift warn'}>
            {shiftOpen ? 'syif dibuka' : 'tiada syif'}
          </span>
        </div>

        <div className="bar-actions">
          {shiftOpen ? (
            <button type="button" className="ghost" onClick={() => setShiftModal('close')}>
              Tutup syif
            </button>
          ) : (
            <button type="button" className="ghost" onClick={() => setShiftModal('open')}>
              Buka syif
            </button>
          )}
          <button type="button" className="ghost" onClick={() => void openDrawerNow()}>
            Buka laci
          </button>
          <button type="button" className="ghost" onClick={() => void openSalesList()}>
            Jualan
          </button>
          <button
            type="button"
            className="ghost"
            onClick={() => void sync()}
            disabled={pending === 0}
          >
            Sync
          </button>

          <span className="bar-sep" aria-hidden="true" />

          <button type="button" className="ghost" onClick={() => void refreshCatalog()}>
            Segarkan katalog
          </button>
          <button type="button" className="ghost" onClick={() => void reloadSettings()}>
            Muatkan tetapan
          </button>
          <button
            type="button"
            className="ghost"
            onClick={() => {
              // Terminal berkongsi: kasir tidak boleh mewarisi sesi pemilik. Membuka admin
              // sentiasa memerlukan kata laluan, dan e-mel diisi semula daripada storan setempat.
              clearAdminSession();
              onAdmin();
            }}
          >
            Admin
          </button>
          <button type="button" className="ghost" onClick={onReset}>
            Peranti
          </button>
        </div>
      </header>

      {pendingApproval && (
        <div className="blocked pending">
          <strong>Peranti ini menunggu kelulusan pengurus.</strong> Jualan belum boleh direkodkan.
          Minta pengurus membuka app.72on.com/admin → Peranti → Luluskan, kemudian muat semula
          halaman ini.
        </div>
      )}
      {revoked && (
        <div className="blocked">
          <strong>Peranti ini telah dicabut.</strong> Jualan tidak boleh direkodkan ke pelayan, jadi
          jualan dihentikan pada PC ini. Jualan yang belum dihantar masih tersimpan pada PC ini.
          <p>
            Minta kod gantian daripada pemilik, kemudian pasang semula PC ini. Jualan yang tertunggak
            akan dihantar selepas peranti diluluskan.
          </p>
          <button type="button" onClick={() => void beginRepair()}>
            Pasang semula peranti
          </button>
        </div>
      )}
      {salesOpen && (
        <div className="modal">
          <div className="sheet">
            <h3>{previewEntry === null ? 'Jualan terkini' : 'Pratonton resit'}</h3>
            <p className="muted">
              {previewEntry === null
                ? 'Senarai ini disimpan pada PC ini. Cetak semula tidak membuka laci dan tidak mengubah rekod jualan.'
                : 'Periksa resit di bawah sebelum mencetak. Ini kandungan yang sama yang akan keluar pada kertas.'}
            </p>
            {previewEntry !== null ? (
              <>
                <div className="receipt-preview">
                  {receiptHeader.length === 0 ? (
                    <p className="rp-note">Kepala resit diambil daripada tetapan ejen.</p>
                  ) : (
                    receiptHeader.map((line) => (
                      <div key={line} className="rp-center rp-bold">
                        {line}
                      </div>
                    ))
                  )}
                  <div className="rp-center rp-bold">RESIT</div>
                  <div className="rp-row">
                    <span>No</span>
                    <span>{previewEntry.sale.receipt_no}</span>
                  </div>
                  <div className="rp-row">
                    <span>Masa</span>
                    <span>
                      {new Date(previewEntry.sale.client_created_at).toLocaleString('ms-MY', {
                        timeZone: 'Asia/Kuala_Lumpur',
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                  {(previewEntry.sale.cashier_name ?? cashier) !== '' && (
                    <div className="rp-row">
                      <span>Kasir</span>
                      <span>{previewEntry.sale.cashier_name ?? cashier}</span>
                    </div>
                  )}
                  <div className="rp-sep" />
                  {previewEntry.lines.map((line) => (
                    <div key={line.id} className="rp-item">
                      <div>{line.description_snapshot}</div>
                      <div className="rp-row">
                        <span>
                          {Number(line.qty)} {line.unit} × RM{formatMYR(line.unit_price_sen)}
                        </span>
                        <span>RM{formatMYR(line.line_total_sen)}</span>
                      </div>
                    </div>
                  ))}
                  <div className="rp-sep" />
                  {previewEntry.sale.discount_sen > 0 && (
                    <div className="rp-row">
                      <span>Diskaun</span>
                      <span>-RM{formatMYR(previewEntry.sale.discount_sen)}</span>
                    </div>
                  )}
                  {previewEntry.sale.tax_sen > 0 && (
                    <div className="rp-row">
                      <span>Cukai</span>
                      <span>RM{formatMYR(previewEntry.sale.tax_sen)}</span>
                    </div>
                  )}
                  <div className="rp-row rp-bold">
                    <span>JUMLAH</span>
                    <span>RM{formatMYR(previewEntry.sale.total_sen)}</span>
                  </div>
                  {previewEntry.payments.map((payment) => (
                    <div key={payment.id} className="rp-row">
                      <span>{payment.method === 'cash' ? 'Tunai' : payment.method}</span>
                      <span>RM{formatMYR(payment.amount_sen)}</span>
                    </div>
                  ))}
                  <div className="rp-row">
                    <span>Baki</span>
                    <span>RM{formatMYR(previewEntry.sale.change_sen)}</span>
                  </div>
                  {receiptFooter !== '' && (
                    <>
                      <div className="rp-sep" />
                      <div className="rp-center">{receiptFooter}</div>
                    </>
                  )}
                </div>
                {previewEntry.sale.synced_at === null && (
                  <p className="muted">
                    Jualan ini belum dihantar ke pelayan. Resit tetap sah dan akan dicetak sama.
                  </p>
                )}
                <div className="actions">
                  <button type="button" className="ghost" onClick={() => setPreviewEntry(null)}>
                    Kembali
                  </button>
                  <button
                    type="button"
                    disabled={reprintBusy !== null}
                    onClick={() => void reprintFromList(previewEntry)}
                  >
                    {reprintBusy !== null ? 'Mencetak…' : 'Cetak resit'}
                  </button>
                </div>
              </>
            ) : salesBusy ? (
              <p className="muted">Memuatkan…</p>
            ) : salesRows.length === 0 ? (
              <p className="muted">Belum ada jualan pada peranti ini.</p>
            ) : (
              <ul className="sale-list">
                {salesRows.map((entry) => (
                  <li key={entry.sale.id} className="sale-row">
                    <div>
                      <strong>{entry.sale.receipt_no}</strong>
                      <p className="muted">
                        {new Date(entry.sale.client_created_at).toLocaleString('ms-MY', {
                          timeZone: 'Asia/Kuala_Lumpur',
                          hour: '2-digit',
                          minute: '2-digit',
                          day: '2-digit',
                          month: '2-digit',
                        })}{' '}
                        · RM{formatMYR(entry.sale.total_sen)}
                        {entry.sale.synced_at === null ? ' · belum dihantar' : ''}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="ghost"
                      onClick={() => setPreviewEntry(entry)}
                    >
                      Pratonton
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {previewEntry === null && (
              <div className="actions">
                <button type="button" className="ghost" onClick={() => setSalesOpen(false)}>
                  Tutup
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {message !== null && (
        <div className="toast" onClick={() => setMessage(null)}>
          {message}
        </div>
      )}

      <main className="body">
        <section className="left">
          <input
            className="search"
            placeholder="Cari nama, SKU atau barcode…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            autoFocus
          />
          <div className="grid">
            {variants.map((variant) => (
              <button key={variant.id} type="button" className="tile" onClick={() => addVariant(variant)}>
                <span className="tile-name">{variant.product_name}</span>
                {variant.name !== null && <span className="tile-variant">{variant.name}</span>}
                <span className="tile-price">
                  RM{formatMYR(variant.price_sen)}
                  {variant.unit === 'pcs' ? '' : `/${variant.unit}`}
                </span>
              </button>
            ))}
            {variants.length === 0 && (
              <p className="muted">
                Tiada barang. Tambah produk di back office, kemudian tekan “Segarkan katalog”.
              </p>
            )}
          </div>
        </section>

        <aside className="right">
          <h2>Resit</h2>
          {!shiftOpen && (
            <p className="error">
              Syif belum dibuka. Buka syif sebelum menjual supaya wang dan diskaun boleh dijejak
              kepada seseorang.
            </p>
          )}
          {cart.length === 0 ? (
            <p className="muted">Belum ada barang.</p>
          ) : (
            <ul className="cart">
              {cart.map((line) => (
                <li key={line.key}>
                  <span>
                    {line.description}
                    <em>
                      {line.qtyMilli === Math.round(line.qtyMilli / 1000) * 1000
                        ? `${line.qtyMilli / 1000}`
                        : (line.qtyMilli / 1000).toFixed(3)}{' '}
                      {line.unit} × RM{formatMYR(line.unitPriceSen)}
                    </em>
                    {line.discountSen > 0 && (
                      <em className="discount-note">
                        Diskaun -RM{formatMYR(line.discountSen)} · {line.discountReason}
                      </em>
                    )}
                  </span>
                  <span className="cart-actions">
                    <button
                      type="button"
                      className="qty-btn"
                      aria-label={`Kurangkan ${line.description}`}
                      onClick={() => changeQty(line.key, -1)}
                    >
                      −
                    </button>
                    <button
                      type="button"
                      className="qty-btn"
                      aria-label={`Tambah ${line.description}`}
                      onClick={() => changeQty(line.key, 1)}
                    >
                      +
                    </button>
                    <button type="button" className="link" onClick={() => openDiscount(line)}>
                      diskaun
                    </button>
                    <button type="button" className="link" onClick={() => removeLine(line.key)}>
                      buang
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          )}

          {totals !== null && (
            <dl className="totals">
              <dt>Subtotal</dt>
              <dd>RM{formatMYR(totals.subtotalSen)}</dd>
              {totals.taxSen > 0 && (
                <>
                  <dt>{taxInclusive ? 'Termasuk cukai' : 'SST'}</dt>
                  <dd>RM{formatMYR(totals.taxSen)}</dd>
                </>
              )}
              {totals.discountSen > 0 && (
                <>
                  <dt>Diskaun</dt>
                  <dd>-RM{formatMYR(totals.discountSen)}</dd>
                </>
              )}
              <dt className="grand">Jumlah</dt>
              <dd className="grand">RM{formatMYR(totals.totalSen)}</dd>
            </dl>
          )}

          <button
            type="button"
            className="pay"
            disabled={totals === null || revoked || pendingApproval || !shiftOpen}
            onClick={() => setPaying(true)}
          >
            Bayar
          </button>
        </aside>
      </main>

      {weightFor !== null && (
        <div className="modal">
          <div className="sheet">
            <h3>{weightFor.product_name} — berat</h3>
            <p className="muted">
              RM{formatMYR(weightFor.price_sen)}/{weightFor.unit}. Masukkan gram.
            </p>
            <input
              autoFocus
              inputMode="decimal"
              value={weightInput}
              onChange={(event) => setWeightInput(event.target.value)}
              placeholder="753"
            />
            <div className="actions">
              <button type="button" className="ghost" onClick={() => setWeightFor(null)}>
                Batal
              </button>
              <button type="button" onClick={addWeighted}>
                Tambah
              </button>
            </div>
          </div>
        </div>
      )}

      {paying && totals !== null && (
        <div className="modal">
          <div className="sheet">
            <h3>Bayaran — RM{formatMYR(totals.totalSen)}</h3>
            <div className="methods">
              {(['cash', 'duitnow_qr', 'card', 'ewallet'] as PaymentMethod[]).map((value) => (
                <button
                  key={value}
                  type="button"
                  className={method === value ? 'method active' : 'method'}
                  onClick={() => setMethod(value)}
                >
                  {value === 'cash' ? 'Tunai' : value === 'duitnow_qr' ? 'DuitNow QR' : value === 'card' ? 'Kad' : 'E-wallet'}
                </button>
              ))}
            </div>
            {method === 'cash' && (
              <input
                inputMode="decimal"
                value={paidInput}
                onChange={(event) => setPaidInput(event.target.value)}
                placeholder={(totals.totalSen / 100).toFixed(2)}
              />
            )}
            <div className="actions">
              <button type="button" className="ghost" onClick={() => setPaying(false)}>
                Batal
              </button>
              <button type="button" disabled={busy} onClick={() => void completeSale()}>
                {busy ? 'Menyimpan…' : 'Selesaikan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {discountFor !== null && (
        <div className="modal">
          <div className="sheet">
            <h3>Diskaun — {discountFor.description}</h3>
            <p className="muted">
              Harga asal RM{formatMYR(discountFor.unitPriceSen)} ×{' '}
              {discountFor.qtyMilli / 1000} {discountFor.unit}. Diskaun direkod berasingan, jadi resit
              menunjukkan harga asal dan potongan.
            </p>
            <label>
              Diskaun (RM)
              <input
                inputMode="decimal"
                value={discountAmount}
                onChange={(event) => setDiscountAmount(event.target.value)}
                placeholder="4.00"
                autoFocus
              />
            </label>
            <label>
              Sebab
              <select value={discountReason} onChange={(event) => setDiscountReason(event.target.value)}>
                <option>Pelanggan tetap</option>
                <option>Harga pasaran lebih rendah</option>
                <option>Barang sedikit rosak</option>
                <option>Promosi</option>
                <option>Lain-lain</option>
              </select>
            </label>
            <label>
              PIN pengurus
              <input
                type="password"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={discountPin}
                onChange={(event) => setDiscountPin(event.target.value)}
                placeholder="PIN kelulusan"
              />
            </label>
            {discountError !== null && <p className="error">{discountError}</p>}
            <p className="muted">
              PIN kelulusan ditetapkan di back office → Staf. Ia tidak boleh digunakan untuk log masuk.
            </p>
            <div className="actions">
              <button type="button" className="ghost" onClick={() => setDiscountFor(null)}>
                Tutup
              </button>
              <button type="button" disabled={discountBusy} onClick={() => void applyDiscount()}>
                {discountBusy ? 'Mengesahkan…' : 'Simpan diskaun'}
              </button>
            </div>
          </div>
        </div>
      )}

      {shiftModal !== null && (
        <div className="modal">
          <div className="sheet">
            <h3>{shiftModal === 'open' ? 'Buka syif' : 'Tutup syif'}</h3>
            <p className="muted">
              {shiftModal === 'open'
                ? 'Masukkan tunai awal dalam laci.'
                : 'Kira tunai fizikal dalam laci dan masukkannya. Z-report akan dicetak, dan kasir akan dilog keluar supaya kasir seterusnya memilih namanya sendiri.'}
            </p>
            <input
              autoFocus
              inputMode="decimal"
              value={cashInput}
              onChange={(event) => setCashInput(event.target.value)}
              placeholder="0.00"
            />
            <div className="actions">
              <button type="button" className="ghost" onClick={() => setShiftModal(null)}>
                Batal
              </button>
              <button
                type="button"
                onClick={() => void (shiftModal === 'open' ? openShiftNow() : closeShiftNow())}
              >
                {shiftModal === 'open' ? 'Buka' : 'Tutup & cetak'}
              </button>
            </div>
          </div>
        </div>
      )}

      {receipt !== null && (
        <div className="modal">
          <div className="sheet receipt">
            <h3>Terima kasih</h3>
            <p className="receipt-no">{receipt.sale.receipt_no}</p>
            <dl className="totals">
              <dt>Jumlah</dt>
              <dd>RM{formatMYR(receipt.sale.total_sen)}</dd>
              <dt>Dibayar</dt>
              <dd>RM{formatMYR(receipt.sale.paid_sen)}</dd>
              <dt>Baki</dt>
              <dd>RM{formatMYR(receipt.sale.change_sen)}</dd>
            </dl>
            {printNote !== null && <p className="muted">{printNote}</p>}
            <div className="actions">
              <button type="button" className="ghost" onClick={() => void printSale(receipt, false, true)}>
                Cetak semula
              </button>
              <button type="button" onClick={() => setReceipt(null)}>
                Jualan baharu
              </button>
            </div>
          </div>
        </div>
      )}

      {closedShift !== null && (
        <div className="modal">
          <div className="sheet">
            <h3>Syif ditutup</h3>
            <dl className="totals">
              <dt>Jualan</dt>
              <dd>{closedShift.summary.sales_count} bil</dd>
              <dt>Jumlah jualan</dt>
              <dd>RM{formatMYR(closedShift.summary.sales_total_sen)}</dd>
              {closedShift.summary.discount_sen > 0 && (
                <>
                  <dt>Diskaun diberi</dt>
                  <dd>-RM{formatMYR(closedShift.summary.discount_sen)}</dd>
                </>
              )}
              <dt>Tunai sepatutnya</dt>
              <dd>RM{formatMYR(closedShift.shift.expected_cash_sen ?? 0)}</dd>
              <dt>Tunai dikira</dt>
              <dd>RM{formatMYR(closedShift.shift.counted_cash_sen ?? 0)}</dd>
              <dt className="grand">Beza</dt>
              <dd className="grand">RM{formatMYR(closedShift.shift.variance_sen ?? 0)}</dd>
            </dl>
            <button
              type="button"
              onClick={() => {
                setClosedShift(null);
                void finishHandover();
              }}
            >
              Tutup
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
